import { create } from "zustand";
import { AlertsApiResponse } from "../../../../types/alertTypes";

/**
 * The alert list fetchers already pay for a full query. This store keeps the
 * count from that response so the scope line can compare it with one extra
 * query for the account root, without another argument on the fetch signatures.
 */
export interface AlertCountSnapshot {
  groupId: string;
  itemCount: number;
  limited: boolean;
  minStartTimestamp: number | null;
  filterType: string;
  searchParamsString: string;
}

interface AlertsScopeState {
  groupSnapshot: AlertCountSnapshot | null;
  accountSnapshot: AlertCountSnapshot | null;
  setGroupSnapshot: (snapshot: AlertCountSnapshot) => void;
  setAccountSnapshot: (snapshot: AlertCountSnapshot | null) => void;
}

export const useAlertsScopeStore = create<AlertsScopeState>((set) => ({
  groupSnapshot: null,
  accountSnapshot: null,
  setGroupSnapshot: (groupSnapshot) =>
    set((state) => {
      const sameQuery =
        state.groupSnapshot?.groupId === groupSnapshot.groupId &&
        state.groupSnapshot?.filterType === groupSnapshot.filterType &&
        state.groupSnapshot?.searchParamsString ===
          groupSnapshot.searchParamsString;
      return {
        groupSnapshot,
        accountSnapshot: sameQuery ? state.accountSnapshot : null,
      };
    }),
  setAccountSnapshot: (accountSnapshot) => set({ accountSnapshot }),
}));

export const parseAlertCount = (
  response: Partial<AlertsApiResponse> | null | undefined
): Pick<AlertCountSnapshot, "itemCount" | "limited" | "minStartTimestamp"> => {
  const records = response?.records;
  const parsed = Number(response?.item_count);
  const recordCount = Array.isArray(records) ? records.length : 0;
  const itemCount = Number.isFinite(parsed) ? parsed : recordCount;
  const limited =
    Boolean(response?.parameters?.query_limit) && recordCount > 99;
  const minStart = response?.min_start_timestamp;
  return {
    itemCount,
    limited,
    minStartTimestamp:
      typeof minStart === "number" && Number.isFinite(minStart)
        ? minStart
        : null,
  };
};

export const recordGroupAlertSnapshot = (
  response: Partial<AlertsApiResponse> | null | undefined,
  groupId: string | number | null | undefined,
  filterType: string,
  searchParamsString: string
) => {
  const parsed = parseAlertCount(response);
  const id =
    groupId != null && groupId !== "" && groupId !== "undefined"
      ? String(groupId)
      : "";
  useAlertsScopeStore.getState().setGroupSnapshot({
    groupId: id,
    filterType,
    searchParamsString,
    ...parsed,
  });
};
